import { ANNOUNCE_BAR } from './announce-bar.const';

const MS_IN_HOUR = 1000 * 60 * 60;
const MS_IN_DAY = MS_IN_HOUR * 24;

const TICK_INTERVAL = 60000;

export type Countdown = {
  isActive: Ref<boolean>;
  days: Ref<number>;
  hours: Ref<number>;
};

/*
  Обратный отсчёт в днях и часах.

  Первый рендер считается и на сервере: иначе шапка приезжает однострочной
  и перестраивается после гидрации. Чтобы разметка совпала, момент «сейчас»
  берётся из payload, а по-настоящему пересчёт идёт уже в onMounted.
*/
export const useCountdown = (targetDate: string): Countdown => {
  const targetTime = new Date(targetDate).getTime();
  const isValidTarget = !Number.isNaN(targetTime);

  const renderedAt = useState('announceRenderedAt', () => Date.now());

  const getRemaining = (now: number) =>
    isValidTarget ? targetTime - now : Number.NaN;

  const isActive = ref(getRemaining(renderedAt.value) > 0);
  const days = ref(0);
  const hours = ref(0);

  const applyRemaining = (remaining: number) => {
    /* Не скрываем неполный последний час: 00:59 должно отображаться как 1 час. */
    const totalHours = Math.ceil(remaining / MS_IN_HOUR);

    days.value = Math.floor(totalHours / (MS_IN_DAY / MS_IN_HOUR));
    hours.value = totalHours % (MS_IN_DAY / MS_IN_HOUR);
  };

  if (isActive.value) {
    applyRemaining(getRemaining(renderedAt.value));
  }

  let timer: ReturnType<typeof setInterval> | null = null;

  const stop = () => {
    if (timer === null) return;

    clearInterval(timer);
    timer = null;
  };

  /* false = отсчёт закончился, пересчитывать больше нечего */
  const recalculate = (): boolean => {
    const remaining = getRemaining(Date.now());

    if (!(remaining > 0)) {
      isActive.value = false;
      days.value = 0;
      hours.value = 0;
      return false;
    }

    isActive.value = true;
    applyRemaining(remaining);

    return true;
  };

  const tick = () => {
    if (!recalculate()) stop();
  };

  /* После сна или неактивной вкладки interval мог не тикать */
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') tick();
  };

  onMounted(() => {
    if (!recalculate()) return;

    timer = setInterval(tick, TICK_INTERVAL);
    document.addEventListener('visibilitychange', onVisibilityChange);
  });

  onBeforeUnmount(() => {
    stop();
    document.removeEventListener('visibilitychange', onVisibilityChange);
  });

  return { isActive, days, hours };
};

export const useAnnounceBar = () => {
  if (!ANNOUNCE_BAR.enabled) {
    return { isVisible: computed(() => false), days: ref(0), hours: ref(0) };
  }

  const { isActive, days, hours } = useCountdown(ANNOUNCE_BAR.startsAt);

  return { isVisible: isActive, days, hours };
};
